import React, { useState, useEffect } from 'react';
import { ChevronUp, Mail, Github, Linkedin, Facebook, Twitter, Instagram, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import ProjectDetail from './ProjectDetail';

const Portfolio = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);

      const sections = ['home', 'about', 'skills', 'education', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    // Scroll to projects section smoothly
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBackToGallery = () => {
    setSelectedProject(null);
  };

  const skills = [
  { name: 'C Language', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
  { name: 'Java', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
  { name: 'JavaScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
  { name: 'Python', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
  { name: 'React JS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
  { name: 'Spring Boot', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg' },
  { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
  { name: 'Linux', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg' },
  { name: 'AWS Cloud', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg' },
  { name: 'CI/CD', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/githubactions/githubactions-original.svg' },
  { name: 'Docker', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
  { name: 'Playwright', logo: 'https://playwright.dev/img/playwright-logo.svg' }
  ];

  const education = [
    {
      degree: 'Bachelor of Science in Information Technology',
      school: 'Cebu Institute of Technology – University',
      location: 'Natalio B. Bacalso Ave, Cebu City, 6000 Cebu',
      period: '2020 – Present',
      level: 'College'
    }
  ];

  const projects = [
    {
      id: 1,
      title: 'CIT-U MARKET MATCH',
      description: `Providing a platform that promotes items that may not be well-recognized within the campus. It also allows students to easily find products available from other students on campus. Additionally, the application ensures that buyers can verify the legitimacy of the items through setting up meet-ups within the school premises, creating a safer and more trustworthy buying experience. This solution directly addresses the visibility and sales challenges for student entrepreneurs, while offering convenience and security for buyers.`,
      image: '/Images/market-match.png',
      github: 'https://github.com/Espina28/CIT-U-MARKET-MATCH',
      liveDemo: null,
      tech: ['React', 'Springboot', 'MYSQL']
    },
    {
      id: 2,
      title: 'TOOL TRACK',
      description: `
      ToolTrack: Smart Solutions for Seamless Tool Management is a user-friendly app developed to track tool borrowing and returns. Both the web and mobile platforms make it easy for users to check tool availability, borrowing history, and maintenance schedules.
      The system provides safe access to users through login and biometric authentication. It allows users to scan QR codes to easily find and verify tool details. Features such as borrowing requests, return processing, and notifications keep things running smoothly and keep users informed.
      ToolTrack also provides analytics on how tools are being used and helps in scheduling maintenance and reservations. The system, therefore, makes tool management easier for organizations, saving them time, improving accountability, and keeping resources well-managed.
      `,
      image: '/Images/tooltrack.png',
      github: 'https://github.com/pawekz/IT342_ToolTrack',
      liveDemo: 'https://tooltrack-frontend-qybsm.ondigitalocean.app/',
      tech: ['ReactJs', 'Springboot', 'MySQL', 'AWS', 'Docker']
    },
    {
      id: 3,
      title: 'EVENTEASE',
      description: `
      EventEase: A Comprehensive Event Management Platform is a user-friendly app developed to manage events and activities. It provides a seamless experience for users to create, update, and delete events, as well as view event details and RSVP to events.
      The system provides safe access to users through login and biometric authentication. It allows users to scan QR codes to easily find and verify event details. Features such as event creation, update, and deletion keep things running smoothly and keep users informed.
      EventEase also provides analytics on how events are being used and helps in scheduling maintenance and reservations. The system, therefore, makes event management easier for organizations, saving them time, improving accountability, and keeping resources well-managed.
      `,
      image: '/Images/eventease.png',
      github: 'https://github.com/Espina28/EventEase-Capstone',
      liveDemo: 'https://www.eventsease.app/',
      tech: ['ReactJs', 'Springboot', 'MySQL', 'AWS', 'Docker', 'Cloudflare', 'Github Actions']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#c3cfe2]">
      {/* Fixed Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.1)] z-[1000] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-8 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
            Aeron Espina
          </div>
          <div className="flex gap-8">
            {['home', 'about', 'skills', 'education', 'projects', 'contact'].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                className={`relative py-2 text-base font-medium capitalize transition-colors duration-300 ${
                  activeSection === section
                    ? 'text-[#667eea] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-gradient-to-r after:from-[#667eea] after:to-[#764ba2]'
                    : 'text-gray-600 hover:text-[#667eea]'
                }`}
              >
                {section}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center px-8 pt-24 pb-16 bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center w-full animate-[fadeInUp_0.8s_ease]">
          <div>
            <h1 className="text-5xl font-bold text-gray-800 mb-4 leading-tight">
              Hi, I'm <span className="bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">Aeron Clyde N. Espina</span>
            </h1>
            <p className="text-2xl text-gray-600 mb-6 font-medium">
              Aspiring Software Developer and Cloud Engineer
            </p>
            <p className="text-lg text-gray-500 leading-relaxed mb-8">
              Bachelor of Science in Information Technology student at Cebu Institute of Technology – University,
              passionate about creating innovative solutions through code.
            </p>
            <div className="flex gap-4">
              <Button
                onClick={() => scrollToSection('projects')}
                className="bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white px-8 py-3 text-base transition-all duration-300 hover:translate-y-[-2px] hover:shadow-[0_10px_25px_rgba(102,126,234,0.4)]"
              >
                View My Work
              </Button>
              <Button
                onClick={() => scrollToSection('contact')}
                variant="outline"
                className="border-2 border-[#667eea] text-[#667eea] px-8 py-3 text-base transition-all duration-300 hover:bg-[#667eea] hover:text-white hover:translate-y-[-2px]"
              >
                Get In Touch
              </Button>
            </div>
          </div>
          <div className="flex justify-center animate-[fadeIn_1s_ease]">
            <div className="w-[350px] h-[350px] rounded-full overflow-hidden shadow-[0_20px_60px_rgba(102,126,234,0.3)] border-[5px] border-white transition-all duration-300 hover:scale-105 hover:shadow-[0_25px_70px_rgba(102,126,234,0.4)]">
              <img
                src="/Images/my_resume2x2.jpg"
                alt="Aeron Espina"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-white">
        <div className="max-w-7xl mx-auto px-8 py-16">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
            About Me
          </h2>
          <div className="grid gap-8">
            <Card className="border-none shadow-[0_10px_40px_rgba(0,0,0,0.08)] transition-all duration-300 hover:translate-y-[-5px] hover:shadow-[0_15px_50px_rgba(0,0,0,0.12)]">
              <CardContent className="p-8">
                <p className="text-lg text-gray-600 leading-relaxed">
                  I am a final-year BSIT student at Cebu Institute of Technology University with a strong specialization in cloud architecture and backend development. As an AWS Academy Graduate in Cloud Architecting, I focus on bridging the gap between robust software engineering and scalable infrastructure. My technical toolkit is built around Spring Boot, React.js, and Docker, allowing me to engineer full-stack applications that are both powerful and user-friendly. I have hands-on experience designing cloud infrastructure on AWS (EC2, S3, RDS, Lightsail) and implementing automated CI/CD pipelines. Currently, I am seeking an intensive OJT opportunity to apply these skills in a production environment.
                </p>
              </CardContent>
            </Card>
            {/* <div className="grid gap-4">
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg transition-colors duration-300 hover:bg-gray-100">
                <span className="font-semibold text-gray-800 min-w-[120px]">Location:</span>
                <span className="text-gray-600">Talisay City, Cebu</span>
              </div>
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg transition-colors duration-300 hover:bg-gray-100">
                <span className="font-semibold text-gray-800 min-w-[120px]">Email:</span>
                <span className="text-gray-600">aeronxespina@gmail.com</span>
              </div>
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg transition-colors duration-300 hover:bg-gray-100">
                <span className="font-semibold text-gray-800 min-w-[120px]">Date of Birth:</span>
                <span className="text-gray-600">March 28, 2001</span>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-8 py-16">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
            Technical Skills
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
            {skills.map((skill, index) => (
              <div
                key={index}
                className="flex flex-col items-center gap-4 p-6 bg-white rounded-xl shadow-[0_5px_20px_rgba(0,0,0,0.06)] transition-all duration-300 hover:translate-y-[-5px] hover:shadow-[0_10px_30px_rgba(0,0,0,0.12)] group"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] flex items-center justify-center transition-transform duration-[600ms] group-hover:rotate-[360deg]">
                  <img
                    src={skill.logo}
                    alt={skill.name}
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <span className="text-base font-semibold text-gray-800 text-center">{skill.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="bg-white">
        <div className="max-w-7xl mx-auto px-8 py-16">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
            Education
          </h2>
          <div className="relative pl-8">
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#667eea] to-[#764ba2]"></div>
            {education.map((edu, index) => (
              <div key={index} className="relative mb-8">
                <div className="absolute left-[-2.5rem] top-6 w-4 h-4 rounded-full bg-white border-[3px] border-[#667eea] shadow-[0_0_0_4px_rgba(102,126,234,0.2)]"></div>
                <Card className="ml-8 border-none shadow-[0_5px_20px_rgba(0,0,0,0.08)] transition-all duration-300 hover:translate-x-[10px] hover:shadow-[0_10px_30px_rgba(0,0,0,0.12)]">
                  <CardContent className="p-6">
                    <div className="inline-block px-3 py-1 bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] text-[#667eea] rounded-full text-sm font-semibold mb-3">
                      {edu.level}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{edu.degree}</h3>
                    <p className="text-base text-gray-600 font-medium mb-1">{edu.school}</p>
                    <p className="text-sm text-gray-500 mb-2">{edu.location}</p>
                    <p className="text-sm text-[#667eea] font-semibold">{edu.period}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-8 py-16">
          {!selectedProject ? (
            <>
              {/* PROJECT GALLERY VIEW */}
              <div className="animate-[fadeIn_0.4s_ease-out]">
                <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
                  Featured Projects
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                  {projects.map((project) => (
                    <Card
                      key={project.id}
                      className="border-none overflow-hidden shadow-[0_5px_20px_rgba(0,0,0,0.08)] cursor-pointer transition-all duration-300 hover:translate-y-[-10px] hover:shadow-[0_15px_40px_rgba(0,0,0,0.15)] group"
                      onClick={() => handleProjectClick(project)}
                    >
                      <div className="w-full h-[200px] overflow-hidden bg-gray-50">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">{project.title}</h3>
                        <p className="text-[0.95rem] text-gray-600 leading-relaxed mb-4">{project.description}</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {project.tech.map((tech, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] text-[#667eea] rounded-full text-xs font-semibold"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-[#667eea] font-semibold transition-all duration-300 group-hover:gap-3">
                          <Github className="w-5 h-5" />
                          <span>View Details</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* PROJECT SHOWCASE DETAIL VIEW */}
              <div className="animate-[fadeIn_0.4s_ease-out]">
                <ProjectDetail project={selectedProject} onBack={handleBackToGallery} />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-white">
        <div className="max-w-7xl mx-auto px-8 py-16">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
            Get In Touch
          </h2>
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              I'm currently looking for opportunities and collaborations.
              Feel free to reach out if you'd like to connect!
            </p>
            <div className="mb-8">
              <a
                href="mailto:aeronclyde.espina@gmail.com"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] text-[#667eea] rounded-full text-lg font-semibold transition-all duration-300 hover:bg-gradient-to-r hover:from-[#667eea] hover:to-[#764ba2] hover:text-white hover:translate-y-[-3px]"
              >
                <Mail className="w-6 h-6" />
                <span>aeronclyde.espina@gmail.com</span>
              </a>
            </div>
            <div className="flex justify-center gap-6">
              <a
                href="https://www.linkedin.com/in/aeron-clyde-espina-38a67939a"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] flex items-center justify-center text-[#667eea] transition-all duration-300 hover:bg-gradient-to-r hover:from-[#667eea] hover:to-[#764ba2] hover:text-white hover:translate-y-[-5px] hover:rotate-[360deg]"
                aria-label="LinkedIn"
              >
                <Linkedin />
              </a>
              <a
                href="https://github.com/Espina28"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] flex items-center justify-center text-[#667eea] transition-all duration-300 hover:bg-gradient-to-r hover:from-[#667eea] hover:to-[#764ba2] hover:text-white hover:translate-y-[-5px] hover:rotate-[360deg]"
                aria-label="GitHub"
              >
                <Github />
              </a>
              {/* <a
                href="#"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] flex items-center justify-center text-[#667eea] transition-all duration-300 hover:bg-gradient-to-r hover:from-[#667eea] hover:to-[#764ba2] hover:text-white hover:translate-y-[-5px] hover:rotate-[360deg]"
                aria-label="Twitter"
              >
                <Twitter />
              </a> */}
              {/* <a
                href="#"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#667eea]/[0.12] to-[#764ba2]/[0.12] flex items-center justify-center text-[#667eea] transition-all duration-300 hover:bg-gradient-to-r hover:from-[#667eea] hover:to-[#764ba2] hover:text-white hover:translate-y-[-5px] hover:rotate-[360deg]"
                aria-label="Facebook"
              >
                <Instagram />
              </a> */}

            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white text-center py-8 text-[0.95rem]">
        <p>&copy; 2025 Aeron Clyde N. Espina. All rights reserved.</p>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white shadow-[0_5px_20px_rgba(102,126,234,0.4)] transition-all duration-300 hover:translate-y-[-5px] hover:shadow-[0_10px_30px_rgba(102,126,234,0.5)] z-[999]"
          size="icon"
          aria-label="Scroll to top"
        >
          <ChevronUp />
        </Button>
      )}
    </div>
  );
};

export default Portfolio;